/**
 * Toast Store
 *
 * 애플리케이션 전역 토스트 알림 상태 관리
 * 기존 ToastContext를 대체합니다.
 */

import { create } from 'zustand';

// ============================================================================
// Types
// ============================================================================

export interface AppToast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number; // ms (0 = no auto-close)
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastState {
  toasts: AppToast[];
}

interface ToastActions {
  addToast: (toast: AppToast) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

type ToastStore = ToastState & ToastActions;

// ============================================================================
// Helpers
// ============================================================================

const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

const DEFAULT_DURATION = 4000;

// 타이머 저장소 (Store 외부에 두어 직렬화 문제 방지)
const toastTimers = new Map<string, NodeJS.Timeout>();

// ============================================================================
// Store
// ============================================================================

export const useToastStore = create<ToastStore>()((set, get) => ({
  toasts: [],

  addToast: (toast) => {
    const duration = toast.duration ?? DEFAULT_DURATION;

    set((state) => ({
      toasts: [...state.toasts, toast],
    }));

    // Auto-remove
    if (duration > 0) {
      const timer = setTimeout(() => {
        get().removeToast(toast.id);
      }, duration);
      toastTimers.set(toast.id, timer);
    }
  },

  removeToast: (id) => {
    // 타이머 정리
    const timer = toastTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      toastTimers.delete(id);
    }

    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearToasts: () => {
    // 모든 타이머 정리
    toastTimers.forEach((timer) => clearTimeout(timer));
    toastTimers.clear();

    set({ toasts: [] });
  },
}));

// ============================================================================
// Convenience Functions (Store 외부에서 직접 호출 가능)
// ============================================================================

/**
 * 성공 토스트 표시
 */
export function showSuccessToast(
  message: string,
  options?: Partial<Omit<AppToast, 'id' | 'type' | 'message'>>
) {
  useToastStore.getState().addToast({
    id: generateId(),
    type: 'success',
    message,
    ...options,
  });
}

/**
 * 에러 토스트 표시
 */
export function showErrorToast(
  message: string,
  options?: Partial<Omit<AppToast, 'id' | 'type' | 'message'>>
) {
  useToastStore.getState().addToast({
    id: generateId(),
    type: 'error',
    message,
    ...options,
  });
}

/**
 * 정보 토스트 표시
 */
export function showInfoToast(
  message: string,
  options?: Partial<Omit<AppToast, 'id' | 'type' | 'message'>>
) {
  useToastStore.getState().addToast({
    id: generateId(),
    type: 'info',
    message,
    ...options,
  });
}

/**
 * 경고 토스트 표시
 */
export function showWarningToast(
  message: string,
  options?: Partial<Omit<AppToast, 'id' | 'type' | 'message'>>
) {
  useToastStore.getState().addToast({
    id: generateId(),
    type: 'warning',
    message,
    ...options,
  });
}

// ============================================================================
// Hook (기존 useToast 호환)
// ============================================================================

/**
 * useToast 훅
 *
 * 기존 ToastContext의 useToast와 동일한 API를 제공합니다.
 *
 * @example
 * const { success, error: toastError, info, warning } = useToast();
 * success('작업이 완료되었습니다.');
 * toastError('오류가 발생했습니다.');
 */
export function useToast() {
  return {
    success: showSuccessToast,
    error: showErrorToast,
    info: showInfoToast,
    warning: showWarningToast,
  };
}

// ============================================================================
// Selectors
// ============================================================================

export const selectToasts = (state: ToastStore) => state.toasts;
export const selectToastCount = (state: ToastStore) => state.toasts.length;
